import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Container,
  Alert,
} from '@mui/material';
import {
  ArrowBack,
  Person,
  LocationOn,
  Inventory,
  LocalShipping,
  AttachMoney,
  Schedule,
  Notes,
  Delete,
  Visibility,
  CheckCircle,
  Cancel,
  Phone,
  Save,
  Warning,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

interface Quote {
  id: number;
  reference: string;
  sender_name: string;
  sender_phone: string;
  sender_address: string;
  recipient_name: string;
  recipient_phone: string;
  recipient_address: string;
  package_type: string;
  weight: number;
  dimensions: string;
  value: number;
  description: string;
  service_type: string;
  delivery_date: string;
  delivery_time: string;
  instructions: string;
  zone_id: number;
  zone_name: string;
  price_per_kg: number;
  total_price: number;
  currency: string;
  status: string;
  created_at: string;
  updated_at: string;
}

const QuoteDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [quote, setQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [priceEditing, setPriceEditing] = useState(false);
  const [newPrice, setNewPrice] = useState('');
  const [priceLoading, setPriceLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    fetchQuote();
  }, [id]);

  const fetchQuote = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/quotes/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch quote');
      }

      const data = await response.json();
      setQuote(data);
      setNewStatus(data.status);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'reviewed': return 'info';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string): React.ReactElement | undefined => {
    switch (status) {
      case 'pending': return <Warning />;
      case 'reviewed': return <Visibility />;
      case 'approved': return <CheckCircle />;
      case 'rejected': return <Cancel />;
      default: return undefined;
    }
  };

  const handleStatusUpdate = async () => {
    if (!quote || !newStatus) return;

    setUpdating(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/quotes/${quote.id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        const updatedQuote = await response.json();
        setQuote(updatedQuote);
        setNewStatus('');
        alert(t('quotes.status.updateSuccess'));
      } else {
        throw new Error('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert(t('quotes.status.updateError'));
    } finally {
      setUpdating(false);
    }
  };

  const handlePriceUpdate = async () => {
    if (!quote || !newPrice) return;

    const priceValue = parseFloat(newPrice);
    if (isNaN(priceValue) || priceValue <= 0) {
      alert('Veuillez entrer un prix valide');
      return;
    }

    setPriceLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/quotes/${quote.id}/price`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ total_price: priceValue }),
      });

      if (response.ok) {
        const updatedQuote = await response.json();
        setQuote(updatedQuote);
        setPriceEditing(false);
        setNewPrice('');
        alert('Prix mis à jour avec succès');
      } else {
        throw new Error('Failed to update price');
      }
    } catch (error) {
      console.error('Error updating price:', error);
      alert('Erreur lors de la mise à jour du prix');
    } finally {
      setPriceLoading(false);
    }
  };

  const startPriceEdit = () => {
    if (quote) {
      setNewPrice(quote.total_price.toString());
      setPriceEditing(true);
    }
  };

  const cancelPriceEdit = () => {
    setPriceEditing(false);
    setNewPrice('');
  };

  const handleDeleteQuote = async () => {
    if (!quote) return;

    setDeleting(true);
    setErrorMessage('');
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/quotes/${quote.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        navigate('/quotes');
      } else {
        throw new Error('Failed to delete quote');
      }
    } catch (error) {
      console.error('Error deleting quote:', error);
      setErrorMessage('Failed to delete quote');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <Box textAlign="center">
            <CircularProgress size={48} />
            <Typography variant="body1" sx={{ mt: 2 }}>
              {t('quotes.detail.loading')}
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  if (error || !quote) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <Box textAlign="center">
            <Warning color="error" sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {error ? t('quotes.detail.error') : t('quotes.detail.notFound')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {error || t('quotes.detail.notFoundDescription')}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate('/quotes')}
            >
              {t('quotes.detail.backToQuotes')}
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/quotes')}
          >
            {t('quotes.detail.backToQuotes')}
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={() => setDeleteDialogOpen(true)}
            disabled={deleting}
          >
            {t('quotes.actions.delete')}
          </Button>
        </Box>

        {/* Success/Error Messages */}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {t('quotes.detail.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('quotes.detail.reference')}: {quote.reference}
            </Typography>
          </Box>
          <Chip
            icon={<Warning />}
            label={t(`quotes.status.${quote.status}`)}
            color="default"
            variant="filled"
          />
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} lg={8}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Sender Information */}
            <Card>
              <CardHeader
                avatar={<Person />}
                title={t('quotes.sender.title')}
              />
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {quote.sender_name}
                </Typography>
                <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                  <Phone fontSize="small" />
                  <Typography variant="body2">{quote.sender_phone}</Typography>
                </Box>
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <LocationOn fontSize="small" />
                  <Typography variant="body2">{quote.sender_address}</Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Recipient Information */}
            <Card>
              <CardHeader
                avatar={<Person />}
                title={t('quotes.recipient.title')}
              />
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {quote.recipient_name}
                </Typography>
                <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                  <Phone fontSize="small" />
                  <Typography variant="body2">{quote.recipient_phone}</Typography>
                </Box>
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <LocationOn fontSize="small" />
                  <Typography variant="body2">{quote.recipient_address}</Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Package Information */}
            <Card>
              <CardHeader
                avatar={<LocalShipping />}
                title={t('quotes.package.title')}
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Type
                    </Typography>
                    <Typography variant="body2">{quote.package_type}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Weight
                    </Typography>
                    <Typography variant="body2">{quote.weight} kg</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body2">{quote.description}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Pricing Information */}
            <Card>
              <CardHeader
                avatar={<AttachMoney />}
                title={t('quotes.pricing.title')}
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      {t('quotes.delivery.zone')}
                    </Typography>
                    <Typography variant="body2">{quote.zone_name}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      {t('quotes.pricing.totalPrice')}
                    </Typography>
                    {priceEditing ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          size="small"
                          type="number"
                          value={newPrice}
                          onChange={(e) => setNewPrice(e.target.value)}
                          sx={{ width: '150px' }}
                          InputProps={{
                            endAdornment: <Typography variant="body2">F CFA</Typography>
                          }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          onClick={handlePriceUpdate}
                          disabled={priceLoading}
                          startIcon={<Save />}
                        >
                          {priceLoading ? 'Sauvegarde...' : 'Sauver'}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={cancelPriceEdit}
                          disabled={priceLoading}
                        >
                          Annuler
                        </Button>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6" sx={{ color: 'green', fontWeight: 'bold' }}>
                          {quote.total_price} F CFA
                        </Typography>
                        {quote.status !== 'approved' && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={startPriceEdit}
                            startIcon={<AttachMoney />}
                          >
                            Modifier
                          </Button>
                        )}
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} lg={4}>
          <Card>
            <CardHeader title={t('quotes.status.management')} />
            <CardContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('quotes.status.current')}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    icon={getStatusIcon(quote.status) || undefined}
                    label={t(`quotes.status.${quote.status}`)}
                    color={getStatusColor(quote.status) as any}
                    variant="filled"
                  />
                </Box>
              </Box>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>{t('quotes.status.update')}</InputLabel>
                <Select
                  value={newStatus}
                  label={t('quotes.status.update')}
                  onChange={(e) => setNewStatus(e.target.value)}
                >
                  <MenuItem value="pending">{t('quotes.status.pending')}</MenuItem>
                  <MenuItem value="reviewed">{t('quotes.status.reviewed')}</MenuItem>
                  <MenuItem value="approved">{t('quotes.status.approved')}</MenuItem>
                  <MenuItem value="rejected">{t('quotes.status.rejected')}</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('quotes.status.notes')}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('quotes.status.notesPlaceholder')}
                sx={{ mb: 2 }}
              />

              <Button 
                fullWidth
                variant="contained"
                startIcon={<Save />}
                onClick={handleStatusUpdate}
                disabled={updating || newStatus === quote.status}
              >
                {updating ? t('quotes.status.updating') : t('quotes.status.update')}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="error" />
            {t('quotes.delete.title')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {t('quotes.delete.message', { reference: quote?.reference })} 
            {t('quotes.delete.warning')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
          >
            {t('quotes.delete.cancel')}
          </Button>
          <Button 
            onClick={handleDeleteQuote}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <Delete />}
          >
            {deleting ? t('quotes.delete.deleting') : t('quotes.delete.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default QuoteDetail;
